import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { formatCurrency } from '../../i18n/i18n';

const LOAN_OPTIONS = [
  { amount: 5000, termWeeks: 12, label: 'Small' },
  { amount: 15000, termWeeks: 20, label: 'Medium' },
  { amount: 50000, termWeeks: 30, label: 'Large' },
  { amount: 150000, termWeeks: 40, label: 'Huge' },
];

/**
 * Loan dialog — take out loans and view/repay active loans.
 */
export function openLoanDialog(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: 'Bank Loans',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:8px;min-width:340px;max-height:450px;overflow-y:auto;';

  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:11px;min-height:14px;text-align:center;';

  function render() {
    container.innerHTML = '';
    const state = store.getState();
    const isHardMode = state.company.isHardMode;

    // Active loans
    if (state.loans.length > 0) {
      const label = document.createElement('div');
      label.textContent = 'ACTIVE LOANS';
      label.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;';
      container.appendChild(label);

      for (const loan of state.loans) {
        const card = document.createElement('div');
        card.className = 'card';
        card.style.cssText = 'padding:8px;display:flex;flex-direction:column;gap:4px;';

        const topRow = document.createElement('div');
        topRow.style.cssText = 'display:flex;justify-content:space-between;font-size:12px;';
        topRow.innerHTML = `<strong>${formatCurrency(loan.amount)} loan</strong><span style="color:var(--color-text-muted);">${(loan.interestRate * 100).toFixed(0)}% APR</span>`;
        card.appendChild(topRow);

        const detailRow = document.createElement('div');
        detailRow.style.cssText = 'display:flex;justify-content:space-between;font-size:11px;color:var(--color-text-secondary);';
        detailRow.innerHTML = `<span>${formatCurrency(loan.weeklyPayment)}/wk</span><span>${loan.weeksLeft} weeks left</span>`;
        card.appendChild(detailRow);

        // Remaining bar
        const pct = loan.amount > 0 ? (loan.remaining / (loan.amount * 1.5)) * 100 : 0;
        const bar = document.createElement('div');
        bar.className = 'progress-bar';
        bar.style.height = '6px';
        const fill = document.createElement('div');
        fill.className = 'progress-bar__fill';
        fill.style.cssText = `width:${Math.min(100, pct)}%;background:var(--color-danger);`;
        bar.appendChild(fill);
        card.appendChild(bar);

        const remainRow = document.createElement('div');
        remainRow.style.cssText = 'display:flex;justify-content:space-between;font-size:10px;';
        remainRow.innerHTML = `<span style="color:var(--color-danger);">Remaining: ${formatCurrency(loan.remaining)}</span><span style="color:var(--color-text-muted);">Paid: ${formatCurrency(loan.totalPaid)}</span>`;
        card.appendChild(remainRow);

        // Early repay button
        const canRepay = state.company.funds >= loan.remaining;
        const repayBtn = document.createElement('button');
        repayBtn.className = 'btn btn--success';
        repayBtn.textContent = canRepay ? `Pay off (${formatCurrency(loan.remaining)})` : `Need ${formatCurrency(loan.remaining)}`;
        repayBtn.disabled = !canRepay;
        repayBtn.style.cssText = 'font-size:10px;padding:4px 8px;align-self:flex-end;';
        repayBtn.addEventListener('click', () => {
          const r = store.dispatch({ type: 'REPAY_LOAN', loanId: loan.id });
          if (r.success) {
            statusEl.textContent = 'Loan repaid!';
            statusEl.style.color = 'var(--color-success)';
          }
          render();
        });
        card.appendChild(repayBtn);

        container.appendChild(card);
      }
    }

    // Take new loan
    const canTakeMore = state.loans.length < 3;

    const newLabel = document.createElement('div');
    newLabel.textContent = canTakeMore ? 'TAKE A LOAN' : 'MAXIMUM 3 LOANS';
    newLabel.style.cssText = `font-size:10px;color:${canTakeMore ? 'var(--color-text-muted)' : 'var(--color-warning)'};letter-spacing:1px;margin-top:4px;`;
    container.appendChild(newLabel);

    if (canTakeMore) {
      for (const opt of LOAN_OPTIONS) {
        const baseRate = isHardMode ? 0.12 : 0.06;
        const rate = baseRate + (opt.termWeeks > 26 ? 0.02 : 0) + (opt.amount > 50000 ? 0.02 : 0);
        const totalOwed = Math.round(opt.amount * (1 + rate * (opt.termWeeks / 52)));
        const weeklyPayment = Math.ceil(totalOwed / opt.termWeeks);
        const totalInterest = totalOwed - opt.amount;

        const card = document.createElement('div');
        card.className = 'card';
        card.style.cssText = 'padding:8px;display:flex;justify-content:space-between;align-items:center;gap:8px;';

        const info = document.createElement('div');
        info.style.cssText = 'flex:1;';
        info.innerHTML = `
          <div style="font-size:12px;font-weight:600;">${opt.label}: ${formatCurrency(opt.amount)}</div>
          <div style="font-size:10px;color:var(--color-text-secondary);">${formatCurrency(weeklyPayment)}/wk · ${opt.termWeeks} weeks · ${(rate * 100).toFixed(0)}% APR</div>
          <div style="font-size:9px;color:var(--color-text-muted);">Total interest: ${formatCurrency(totalInterest)}</div>
        `;
        card.appendChild(info);

        const btn = document.createElement('button');
        btn.className = 'btn btn--primary';
        btn.textContent = 'Borrow';
        btn.style.cssText = 'font-size:11px;padding:6px 14px;flex-shrink:0;';
        btn.addEventListener('click', () => {
          const r = store.dispatch({ type: 'TAKE_LOAN', amount: opt.amount, termWeeks: opt.termWeeks });
          if (r.success) {
            statusEl.textContent = `Borrowed ${formatCurrency(opt.amount)}!`;
            statusEl.style.color = 'var(--color-success)';
          } else {
            statusEl.textContent = r.error ?? 'Failed';
            statusEl.style.color = 'var(--color-danger)';
          }
          render();
        });
        card.appendChild(btn);

        container.appendChild(card);
      }
    }
  }

  render();
  container.appendChild(statusEl);
  dialog.body.appendChild(container);
  dialog.mount(parent);
}
